import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

import MediaPreview from 'Components/Cards/MediaPreview';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import store from 'Redux/store';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import 'Components/Cards/LimitedTimeDealCard.scss';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import Drm from 'Components/Utility/Drm';
import DlcBadge from 'Components/Cards/DlcBadge';
import CountdownTimer from 'Components/Utility/CountdownTimer';
import NewCountdownTimer from 'Components/Utility/CountdownTimer/CountdownTimer';
import Price from 'Containers/Utility/Price/Price';
import { findBrowserLang } from 'Config/lang';
import HitCardLabel from 'Components/Cards/HitCardLabel';

const LimitedTimeDealCard = ({
  hit,
}) => {
  const {
    name,
    type,
    slug,
    cover,
    categories,
    price,
    fullPrice,
    display_type: displayType,
    dlc_total: dlcTotal,
    video_clip_poster: videoClipPoster,
    video_clip_files: videoClipFiles,
    age_ratings: ageRatings,
    bundle_covers: bundleCovers,
    pay_what_you_want: payWhatYouWant,
    free_to_play: freeToPlay,
    discount_percent: discountPercent,
    drm,
    operating_systems: operatingSystems,
    sdesc,
    sdescs,
    star_deal: starDeal,
    keysAvailablePercentage,
    available_valid_until: availableValidUntil,
    valid_until: validUntil,
    mystery,
    giveaway,
    bundle_tiers: bundleTiers,
  } = hit;
  const userLang = findBrowserLang();
  const [isHovering, setIsHovering] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const tagsContainerRef = useRef(null);
  const [visableTags, setVisableTags] = useState([]);
  const OS = operatingSystems || [];
  const [mysteryTierIndex, setMysteryTierIndex] = useState(0);
  const isValidUntil = availableValidUntil || validUntil;
  const description = (sdescs && sdescs[userLang]) || sdesc;
  const isBundle = displayType === 'bundle' && mystery !== true;

  let validDrm = '';
  let filteredDrm = [];
  if (hit && Array.isArray(drm)) filteredDrm = drm.filter(d => d !== 'voucher' && d !== 'drm free');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0] || 'steam';

  const onLinkClick = (link) => {
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section: 'LimitedTimeDealCard', value: link },
    });
  };

  useEffect(() => {
    if (categories && categories.length > 0) {
      setVisableTags(categories);
    }
  }, [categories]);

  useEffect(() => {
    const checkVisibility = () => {
      if (!tagsContainerRef.current || !visableTags.length) return;
      const container = tagsContainerRef.current;
      const tagElements = Array.from(container.children);
      const maxWidth = container.clientWidth;
      const GAP_WIDTH = 8; // Gap between tags

      const newVisibleTags = tagElements.reduce((acc, tagElement, index) => {
        // Add gap width for all elements except the first one
        const gapWidth = index > 0 ? GAP_WIDTH : 0;
        const currentWidth = acc.totalWidth + tagElement.offsetWidth + gapWidth;
        if (currentWidth <= maxWidth) {
          acc.totalWidth = currentWidth;
          acc.tags.push(tagElement.innerText);
        }
        return acc;
      }, { totalWidth: 0, tags: [] }).tags;

      if (newVisibleTags.length !== visableTags.length) {
        setVisableTags(newVisibleTags);
      }
    };

    const observer = new ResizeObserver(checkVisibility);
    observer.observe(tagsContainerRef.current);
    checkVisibility(); // Initial check

    return () => observer.disconnect();
  }, [visableTags, categories]);

  return (
    <div className="fan-favourite-card-new">
      <div className="content-container">
        <div
          className="left-container faux-block-link"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div className="card-actions-container d-none d-lg-flex">
            {isHovering &&
              <HitCardWishlistBtn
                productId={hit.product_id}
                slug={slug}
                hit={hit}
                componentType="FanFavouriteCard"
              />
            }
          </div>
          <Link
            to={HitLinkTo(hit)}
            className="w-100 faux-block-link__overlay-link"
            aria-label={`View details for ${name}`}
            onClick={() => onLinkClick(`/${type}/${slug}`)}
          >
            <div className="cover-container">
              <HitCardLabel
                slug={slug}
                type={type}
                displayType={displayType}
                discountPercent={discountPercent}
                isStarDeal={starDeal}
                isGiveaway={giveaway}
              />
              <MediaPreview
                disableVideo
                name={name}
                cover={cover}
                bundleCovers={bundleCovers}
                isHovering={isHovering}
                fullPrice={fullPrice}
                type={type}
                displayType={displayType}
                gameTotal={hit.game_total + dlcTotal}
                videoClipPoster={videoClipPoster}
                videoClipFiles={videoClipFiles}
                ageRatings={ageRatings}
                sizesOverride="(min-width: 1520px) 590px, (min-width: 1200px) 500px, (min-width: 991px) 420px, (min-width: 768px) 310px, 98vw"
              />
            </div>
          </Link>
          <div className="right-container faux-block-link">
            <div className="name-tag-container">
              <div className="name-container">
                <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link game-name" onClick={() => onLinkClick(`/${type}/${slug}`)}>
                  {name}
                </Link>
              </div>
              <div className="tag-column-container" ref={tagsContainerRef}>
                {!isEmpty(visableTags) && visableTags.map(tag =>
                  <div className="tag-column" key={tag}>
                    <div className="tag">
                      {tag}
                    </div>
                  </div>,
                )}
              </div>
            </div>
            {!starDeal &&
              <div className="review-text">
                {description}
              </div>
            }
            {starDeal && !mystery &&
              <>
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated star-deal"
                    role="progressbar"
                    style={keysAvailablePercentage === 0 ? { width: '0%' } : { width: `${keysAvailablePercentage}%` }}
                    aria-valuenow={keysAvailablePercentage === 0 ? 0 : keysAvailablePercentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Keys remaining"
                    title="Keys remaining"
                  />
                  {keysAvailablePercentage >= 85 &&
                    <div className="progress-timer progress-timer-overlay">
                        {keysAvailablePercentage}% &nbsp;<FormattedMessage id="limitedtimedeal.remaininghigh" defaultMessage="Remaining" tagName="span" />
                    </div>
                  }
                  {keysAvailablePercentage < 85 && keysAvailablePercentage >= 20 &&
                    <div className="progress-timer progress-timer-overlay">
                      {keysAvailablePercentage}% &nbsp;<FormattedMessage id="limitedtimedeal.remaining" defaultMessage="keys left" tagName="span" />
                    </div>
                  }
                  {keysAvailablePercentage < 20 &&
                    <div className="progress-timer progress-timer-overlay">
                      <FormattedMessage id="limitedtimedeal.remaininglow" defaultMessage="Last Few Keys Remaining" tagName="span" />
                    </div>
                  }
                </div>
                {keysAvailablePercentage !== 0 &&
                  <div className="stardeal-ends">
                    <CountdownTimer
                      noLabel
                      featuredDeal
                      value={isValidUntil * 1000}
                    />
                  </div>
                }
              </>
            }
            {mystery && bundleTiers &&
              <Dropdown
                isOpen={isOpen}
                toggle={() => setIsOpen(!isOpen)}
                className="mystery-dropdown-container"
              >
                <DropdownToggle
                  className="product-editions-dropdown-toggle"
                >
                  <div className="editions-dropdown-text">
                    <FormattedMessage
                      id="limitedtimedealcard.mysterykeytext"
                      defaultMessage="{count, plural, one {# Mystery Key} other {# Mystery Keys}}"
                      values={{
                        count: bundleTiers[mysteryTierIndex].displayTotal,
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="price-container mr-2">
                      <strong>
                        <Price price={bundleTiers[mysteryTierIndex].price} pence={false} />
                      </strong>
                    </div>
                    <div className="icon">
                      <FontAwesomeIcon icon="chevron-down" />
                    </div>
                  </div>
                </DropdownToggle>
                <DropdownMenu>
                  {bundleTiers.map((bundle, index) => {
                    const displayTotal = bundle.displayTotal;
                    const bundlePrice = bundle.price;

                    return (
                      <DropdownItem
                        key={Math.random()}
                        className="edition-container"
                        onClick={() => setMysteryTierIndex(index)}
                      >
                        <div className="d-flex">
                          <div
                            className="edition-content d-flex align-items-center"
                          >
                            <FormattedMessage
                              id="limitedtimedealcard.mysterykeytext"
                              defaultMessage="{count, plural, one {# Mystery Key} other {# Mystery Keys}}"
                              values={{
                                count: displayTotal,
                              }}
                            />
                          </div>
                          <div className="price-container d-flex align-items-center align-content-center ml-auto">
                            <strong>
                              <Price price={bundlePrice} pence={false}/>
                            </strong>
                          </div>
                        </div>
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              </Dropdown>
            }
            <div className="hitCardStripe__content">
              <div className="hitCardStripe__content__purchase">
                <div className="drm-container">
                  {validDrm &&
                    <Drm drmType={validDrm} />
                  }
                  <div className="drm-icons">
                    {OS && OS.includes('windows') &&
                      <FontAwesomeIcon icon={['fab', 'windows']} size="lg" className="mr-2" />
                    }
                    {OS && OS.includes('mac') &&
                      <FontAwesomeIcon icon={['fab', 'apple']} size="lg" className="mr-2" />
                    }
                    {OS && OS.includes('linux') &&
                      <FontAwesomeIcon icon={['fab', 'linux']} size="lg" />
                    }
                  </div>
                  {(type === 'dlc' || displayType === 'dlc') &&
                    <DlcBadge />
                  }
                </div>
                <div className="price-container">
                  {(hit.presale && freeToPlay) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="limitedtimedealcard.availablesoon" defaultMessage="Available Soon" />
                    </div>
                  }

                  {(freeToPlay || hit.presale) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="limitedtimedealcard.freetoplay" defaultMessage="Free to Play" />
                    </div>
                  }

                  {!hit.presale && !freeToPlay && !hit.hide_price && !mystery && !isBundle &&
                    <PriceContainer
                      discountPercent={discountPercent}
                      discountPercentOverride={false}
                      drm={validDrm}
                      fullPrice={hit.fullPrice}
                      giftButton={false}
                      hideDiscount={hit.hide_discount}
                      lightTheme={false}
                      mystery={hit.mystery}
                      payWhatYouWant={payWhatYouWant}
                      price={price}
                      showAsterisk={!!hit.addVoucher}
                      slug={hit.slug}
                      tiered={hit.tiered}
                      type={hit.type}
                    />
                  }
                  {!isBundle &&
                    <AddToCartBtn
                      newIcon
                      showInCartLink
                      id={hit.product_id}
                      slug={hit.slug}
                      voucherToAdd={hit.addVoucher}
                      buttonText="ADD TO CART"
                      hideButtonText={!mystery}
                      hideIcon={mystery}
                      classNameButton={mystery ? 'mystery' : ''}
                      tier={mystery ? mysteryTierIndex : null}
                    />
                  }
                  {isBundle &&
                    <NewCountdownTimer
                      cardTimer
                      value={isValidUntil * 1000}
                    />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

LimitedTimeDealCard.propTypes = {
  hit: PropTypes.shape({}),
};

LimitedTimeDealCard.defaultProps = {
  hit: {},
};

export default LimitedTimeDealCard;
